import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { Fir5471ScheduleFService } from './fir-5471-schedule-f.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface ScheduleFColumn {
  VALUE: number | null;
  line_attrib_key?: string;
  attrib_name?: string;
  is_editable: boolean;
  is_Display: boolean;
}

interface AdjustmentAmount {
  PY_BALANCE: number;
  CY_BALANCE: number;
  HO_BOY_ADJ_AMT: number;
  BOA_PERIOD: number;
  EOA_PERIOD: number;
  LINE_NO: string;
  ACCT_TYPE: number;
}

interface ScheduleFLineItem {
  line_no: string;
  line_Description: string;
  line_type: 'Assets' | 'Liabilities' | 'NOTES';
  col_a?: ScheduleFColumn;
  col_b?: ScheduleFColumn;
  col_c?: ScheduleFColumn;
  col_d?: ScheduleFColumn;
  col_e?: ScheduleFColumn;
  VALUE?: { VALUE: string | null; line_attrib_key: string; attrib_name: string };
  saveParams?: {
    tax_year: string;
    trans_type_key: string;
    trans_Details_key: string;
    occurrence: string;
    form_key: string;
  };
  backgroudColor?: { background: string; 'font-weight': string } | {} | null;
  startDelimiter?: string;
  endDelimiter?: string;
  multiplier?: number | null;
  line_multiplier?: number | null;
}

@Component({
    selector: 'gtw-fir-5471-schedule-f',
    templateUrl: './fir-5471-schedule-f.component.html',
    styleUrls: ['./fir-5471-schedule-f.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Fir5471ScheduleFComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private unsubscribe$ = new Subject<void>();
  private readonly subscriptions = new Subscription();
  /** Bumps on context reset / reinit so in-flight JSON loads cannot commit stale UI. */
  private workspaceLoadSeq = 0;

  modalTitle = 'Schedule F: Balance Sheet';
  dataLoading = false;
  crudLoading = false;
  userMessage = '';
  minimise = false;
  isInitialized = false;

  irsF5471ScheduleFData: ScheduleFLineItem[] = [];
  irsF5471ScheduleFOriginalData: ScheduleFLineItem[] = [];
  irs5471ScheduleFNotes: ScheduleFLineItem | null = null;
  adjAmts: AdjustmentAmount[] = [];

  line_arr: string[] = [];
  taxYear = '';

  baseURLs: any = {};
  GENERAL_CONFIG: any = {};
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private fir5471ScheduleFService: Fir5471ScheduleFService,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndTryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.rowData = rowObj;
  }

  private resolveApiRoot(): string {
    return (
      this.baseURLs?.api ||
      this.baseURLs?.base_url ||
      this.GENERAL_CONFIG?.base_url ||
      ''
    ).toString();
  }

  private resetAndTryInit(): void {
    this.workspaceLoadSeq++;
    this.isInitialized = false;
    this.tryInit();
    this.cdr.markForCheck();
  }

  tryInit(): void {
    if (this.isInitialized) return;

    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    const apiRoot = this.resolveApiRoot();
    if (
      !apiRoot ||
      !combinationKey ||
      !this.globalService?.globalParams ||
      !this.rowData ||
      Object.keys(this.rowData).length === 0
    ) {
      return;
    }

    this.taxYear = (this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '').toString();
    this.line_arr = parseInt(this.taxYear) < 2018
      ? ['2B', '8B', '9B', '11D', '21']
      : ['2B', '9B', '10B', '12D', '23'];

    this.isInitialized = true;
    this.loadIRSF5471ScheduleFData();
  }

  loadIRSF5471ScheduleFData(): void {
    const seq = this.workspaceLoadSeq;
    this.irsF5471ScheduleFData = [];
    this.irsF5471ScheduleFOriginalData = [];

    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    const params = {
      action_code: 'iiprik',
      irs_form_no: 'F5471',
      combination_key: combinationKey,
      jcd_key: 250
    };

    this.dataLoading = true;

    const apiRoot = this.resolveApiRoot();
    this.jsonObjectFactory
      .getJSONObj(
        apiRoot + '/loadJsonObject?action_code=iiprik',
        params,
        this.globalService,
        null,
        null
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (seq !== this.workspaceLoadSeq) {
          this.dataLoading = false;
          this.cdr.markForCheck();
          return;
        }
        if (data.callSuccess === '1') {
          this.dataLoading = false;
          const irsF5471ScheduleFDataTemp = data.jsonObject || [];
          this.processScheduleFData(irsF5471ScheduleFDataTemp);
          const notesIndex = this.getIndex(null, 'Notes');
          this.irs5471ScheduleFNotes = notesIndex !== null ? this.irsF5471ScheduleFData[notesIndex] : null;
          this.setAdjAmt();
          this.irsF5471ScheduleFOriginalData = _.cloneDeep(this.irsF5471ScheduleFData);
        } else {
          this.dataLoading = false;
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar(
              'Sorry you do not have access to do the requested action.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          } else {
            this.gtwSnackbarService.openSnackBar(
              'An error has occurred, If you continue to experience this issue please visit the help page to contact support.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          }
        }
        this.cdr.markForCheck();
      });
  }

  processScheduleFData(data: any[]): void {
    if (!data || data.length < 2) return;

    let i = 1;
    let previous_line_no = data[i].line_no;

    while (i < data.length) {
      const tempObj: Partial<ScheduleFLineItem> = {};
      let j = i;

      while (j < data.length && previous_line_no === data[j].line_no) {
        const attrParts = data[j].attrib_value?.split('~') || [];
        const attrName = attrParts[0] || '';

        if (attrName === 'LINE_NO') {
          tempObj.line_no = data[j].line_no;
          tempObj.line_multiplier = null;
          tempObj.startDelimiter = '';
          tempObj.endDelimiter = '';
          const objParts = (data[j].object_id || '').split('~');
          tempObj.saveParams = {
            tax_year: objParts[0] || '',
            trans_type_key: objParts[1] || '',
            trans_Details_key: objParts[3] || '',
            occurrence: objParts[4] || '',
            form_key: objParts[6] || ''
          };
        } else if (attrName === 'ASSET' || attrName === 'LIABILITIES_AND_STOCKHOLDER_EQUITY') {
          const desc = attrParts[3] || '';
          if (desc === 'Total liabilities and shareholders equity' || desc === 'Total assets') {
            tempObj.backgroudColor = { background: '#D6D6D6', 'font-weight': 'bold' };
          } else {
            tempObj.backgroudColor = {};
          }
          tempObj.line_Description = desc;
          tempObj.line_type = attrName === 'ASSET' ? 'Assets' : 'Liabilities';
        } else if (attrName === 'NOTES') {
          tempObj.VALUE = {
            VALUE: data[j].VALUE !== undefined ? data[j].VALUE : null,
            line_attrib_key: attrParts[1] || '',
            attrib_name: attrName
          };
          tempObj.line_type = 'NOTES';
          tempObj.line_Description = attrParts[3] || 'Notes';
        } else if (attrName === 'ADJUSTMENT_TO_CURRENT_YEAR_BEGINNING_BALANCE') {
          tempObj.col_c = {
            VALUE: data[j].VALUE !== undefined ? parseInt(data[j].VALUE, 10) : null,
            line_attrib_key: attrParts[1],
            attrib_name: attrName,
            is_editable: data[j].PROPERTY_TYPE === '9',
            is_Display: data[j].PROPERTY_TYPE !== '0'
          };
        } else if (attrName === 'BEGINNING_OF_ANNUAL_ACCOUNTING') {
          tempObj.col_d = {
            VALUE: data[j].VALUE !== undefined ? parseInt(data[j].VALUE, 10) : null,
            line_attrib_key: attrParts[1],
            attrib_name: attrName,
            is_editable: data[j].PROPERTY_TYPE === '9',
            is_Display: data[j].PROPERTY_TYPE !== '0'
          };
        } else if (attrName === 'END_OF_ANNUAL_ACCOUNTING_PERIOD') {
          tempObj.col_e = {
            VALUE: data[j].VALUE !== undefined ? parseInt(data[j].VALUE, 10) : null,
            line_attrib_key: attrParts[1],
            attrib_name: attrName,
            is_editable: data[j].PROPERTY_TYPE === '9',
            is_Display: data[j].PROPERTY_TYPE !== '0'
          };
        }

        const hasCols = tempObj.col_c && tempObj.col_d && tempObj.col_e;
        const allHidden = hasCols &&
          !(tempObj.col_c as ScheduleFColumn).is_Display &&
          !(tempObj.col_d as ScheduleFColumn).is_Display &&
          !(tempObj.col_e as ScheduleFColumn).is_Display;

        if (allHidden) {
          tempObj.col_a = { VALUE: null, is_editable: false, is_Display: false };
          tempObj.col_b = { VALUE: null, is_editable: false, is_Display: false };
        } else {
          tempObj.col_a = { VALUE: null, is_editable: false, is_Display: true };
          tempObj.col_b = { VALUE: null, is_editable: false, is_Display: true };
        }

        previous_line_no = data[j].line_no;
        j++;
      }

      if (j < data.length) previous_line_no = data[j].line_no;

      const idx = this.irsF5471ScheduleFData.length;
      if (tempObj.line_no !== undefined) {
        this.irsF5471ScheduleFData[idx] = tempObj as ScheduleFLineItem;
      }
      i = j;
    }
  }

  setAdjAmt(): void {
    const seq = this.workspaceLoadSeq;
    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    const params = {
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      combination_key: combinationKey,
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      jcd_key: 250
    };

    this.dataLoading = true;

    const apiRoot = this.resolveApiRoot();
    this.jsonObjectFactory
      .getJSONObj(
        apiRoot + '/loadJsonObject?action_code=icq6yn',
        params,
        this.globalService,
        null,
        null
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (seq !== this.workspaceLoadSeq) {
          this.dataLoading = false;
          this.cdr.markForCheck();
          return;
        }
        if (data.callSuccess === '1') {
          this.dataLoading = false;
          this.adjAmts = data.jsonObject || [];
          this.ensureAdjAmtsComplete();
          this.applyAdjAmtsToData();
          this.irsF5471ScheduleFOriginalData = _.cloneDeep(this.irsF5471ScheduleFData);
        } else {
          this.dataLoading = false;
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar(
              'Sorry you do not have access to do the requested action.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          } else {
            this.gtwSnackbarService.openSnackBar(
              'An error has occurred, If you continue to experience this issue please visit the help page to contact support.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          }
        }
        this.cdr.markForCheck();
      });
  }

  private ensureAdjAmtsComplete(): void {
    const temp_arr = parseInt(this.taxYear) < 2018
      ? ['9A', '9B', '11B', '18A', '21', '1', '2A', '2B', '3', '4', '5', '6', '7', '8A', '8B', '10', '11A', '11C', '11D', '12', '14', '15', '16', '17', '18B', '19', '20']
      : ['10A', '10B', '11', '21', '1', '2A', '2B', '3', '4', '5', '6', '7', '8', '9A', '9B', '12A', '12B', '12C', '12D', '14', '15', '16', '17', '18', '19', '20A', '20B', '21', '22', '23', '24'];

    const defaultObj = (acctType: number) => ({
      PY_BALANCE: 0,
      CY_BALANCE: 0,
      HO_BOY_ADJ_AMT: 0,
      BOA_PERIOD: 0,
      EOA_PERIOD: 0,
      LINE_NO: '',
      ACCT_TYPE: acctType
    });

    if (this.adjAmts.length === 0) {
      this.adjAmts = temp_arr.map(ln => ({
        ...defaultObj(parseInt(ln, 10) < 13 ? 1 : 2),
        LINE_NO: ln
      }));
    } else {
      for (const ln of temp_arr) {
        const found = this.adjAmts.some((a: AdjustmentAmount) => a.LINE_NO === ln);
        if (!found) {
          this.adjAmts.push({
            ...defaultObj(parseInt(ln, 10) < 13 ? 1 : 2),
            LINE_NO: ln
          });
        }
      }
    }
  }

  private applyAdjAmtsToData(): void {
    const taxYearNum = parseInt(this.taxYear, 10);
    const retainedLine = taxYearNum < 2018 ? '20' : '22';

    for (let i = 0; i < this.adjAmts.length; i++) {
      const amt = this.adjAmts[i];
      const line_multiplier = amt.ACCT_TYPE === 1 ? 1 : -1;

      for (let j = 0; j < this.irsF5471ScheduleFData.length; j++) {
        const line = this.irsF5471ScheduleFData[j];
        if (line.line_no !== amt.LINE_NO) continue;

        if (this.line_arr.indexOf(line.line_no) !== -1) {
          if (amt.PY_BALANCE !== 0 && line.col_a) {
            line.col_a.VALUE = amt.PY_BALANCE * line_multiplier * -1;
          }
          line.multiplier = line_multiplier;
          line.startDelimiter = '(';
          line.endDelimiter = ')';

          if (amt.CY_BALANCE !== 0 && line.col_b) {
            line.col_b.VALUE = amt.CY_BALANCE * line_multiplier * -1;
          }
          if (amt.EOA_PERIOD !== 0 && line.col_e) {
            line.col_e.VALUE = amt.EOA_PERIOD * -1;
          }
          if (line.col_c && line.col_c.VALUE != null) {
            line.col_c.VALUE = line.col_c.VALUE * -1;
          }
          this.computeRowTotal(line.line_no);
        } else if (line.line_no === retainedLine) {
          let total_eoa = 0;
          let total_py = 0;
          let total_cy = 0;
          for (const a of this.adjAmts) {
            total_eoa += a.EOA_PERIOD;
            total_py += a.PY_BALANCE;
            total_cy += a.CY_BALANCE;
          }
          const pyVal = total_py + amt.PY_BALANCE * line_multiplier;
          const cyVal = total_cy + amt.CY_BALANCE * line_multiplier;
          const eoaVal = total_eoa + amt.EOA_PERIOD * line_multiplier;

          if (line.col_a) line.col_a.VALUE = pyVal !== 0 ? pyVal : null;
          if (line.col_b) line.col_b.VALUE = cyVal !== 0 ? cyVal : null;
          if (line.col_e) line.col_e.VALUE = eoaVal !== 0 ? eoaVal : null;
          if (line.col_c && line.col_c.VALUE != null) {
            line.col_c.VALUE = line.col_c.VALUE * line_multiplier;
          }
          if (line.col_d && line.col_d.VALUE != null) {
            line.col_d.VALUE = line.col_d.VALUE * line_multiplier;
          }
          line.multiplier = line_multiplier;
          this.computeRowTotal(line.line_no);
        } else {
          line.multiplier = line_multiplier;
          if (amt.PY_BALANCE !== 0 && line.col_a) {
            line.col_a.VALUE = amt.PY_BALANCE * line_multiplier;
          }
          if (amt.CY_BALANCE !== 0 && line.col_b) {
            line.col_b.VALUE = amt.CY_BALANCE * line_multiplier;
          }
          if (amt.EOA_PERIOD !== 0 && line.col_e) {
            line.col_e.VALUE = amt.EOA_PERIOD * line_multiplier;
          }
          if (line.col_c && line.col_c.VALUE != null) {
            line.col_c.VALUE = line.col_c.VALUE * line_multiplier;
          }
          this.computeRowTotal(line.line_no);
        }
        break;
      }
    }

    this.computeColTotal('a', '1', 'Assets');
    this.computeColTotal('b', '1', 'Assets');
    this.computeColTotal('c', '1', 'Assets');
    this.computeColTotal('d', '1', 'Assets');
    this.computeColTotal('e', '1', 'Assets');
    this.computeColTotal('a', '15', 'Liabilities');
    this.computeColTotal('b', '15', 'Liabilities');
    this.computeColTotal('c', '15', 'Liabilities');
    this.computeColTotal('d', '15', 'Liabilities');
    this.computeColTotal('e', '15', 'Liabilities');
  }

  getIndex(line_no: string | null, attrib_Description: string | null): number | null {
    if (attrib_Description != null) {
      const idx = this.irsF5471ScheduleFData.findIndex(
        item => item.line_Description === attrib_Description
      );
      return idx >= 0 ? idx : null;
    }
    if (line_no != null) {
      const idx = this.irsF5471ScheduleFData.findIndex(item => item.line_no === line_no);
      return idx >= 0 ? idx : null;
    }
    return null;
  }

  computeRowTotal(line_no: string): void {
    const j = this.getIndex(line_no, null);
    if (j === null) return;

    const line = this.irsF5471ScheduleFData[j];
    let total: number | null = null;

    if (this.line_arr.indexOf(line_no) === -1) {
      if (line.col_b?.VALUE != null) total = line.col_b.VALUE;
      if (line.col_c?.VALUE != null) {
        total = total != null ? total + line.col_c.VALUE : line.col_c.VALUE;
      }
      if (line.col_d) {
        line.col_d.VALUE = total != null ? total : null;
      }
    } else {
      if (line.col_b?.VALUE != null) total = line.col_b.VALUE * -1;
      if (line.col_c?.VALUE != null) {
        total = total != null ? total + (line.col_c.VALUE * -1) : line.col_c.VALUE * -1;
      }
      if (line.col_d) {
        line.col_d.VALUE = total != null ? total * -1 : null;
      }
    }

    this.computeColTotal('d', line.line_no, line.line_type);
    this.computeColTotal('c', line.line_no, line.line_type);
  }

  computeColTotal(section: string, line_no: string, line_type: string): void {
    let i: number;
    let totalLineIndex: number | null = null;

    if (line_type === 'Assets') {
      i = 0;
      totalLineIndex = this.getIndex(null, 'Total assets');
    } else {
      i = this.irsF5471ScheduleFData.findIndex(l => l.line_type === 'Liabilities');
      totalLineIndex = this.getIndex(null, 'Total liabilities and shareholders equity');
    }

    if (i < 0 || totalLineIndex === null) return;

    let total: number | null = null;
    while (i < this.irsF5471ScheduleFData.length - 1 && i < totalLineIndex) {
      const row = this.irsF5471ScheduleFData[i];
      const col = section === 'a' ? row.col_a : section === 'b' ? row.col_b : section === 'c' ? row.col_c : section === 'd' ? row.col_d : row.col_e;
      if (col?.VALUE != null) {
        const val = this.line_arr.indexOf(row.line_no) !== -1 ? col.VALUE * -1 : col.VALUE;
        total = total != null ? total + val : val;
      }
      i++;
    }

    if (totalLineIndex != null && this.irsF5471ScheduleFData[totalLineIndex]) {
      const totalRow = this.irsF5471ScheduleFData[totalLineIndex];
      if (section === 'a' && totalRow.col_a) totalRow.col_a.VALUE = total ?? 0;
      else if (section === 'b' && totalRow.col_b) totalRow.col_b.VALUE = total ?? 0;
      else if (section === 'c' && totalRow.col_c) totalRow.col_c.VALUE = total ?? 0;
      else if (section === 'd' && totalRow.col_d) totalRow.col_d.VALUE = total ?? 0;
      else if (section === 'e' && totalRow.col_e) totalRow.col_e.VALUE = total ?? 0;
    }
  }

  saveScheduleFDetails(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar(
        'Please wait while we save this request',
        'gtw-snackbar--yellow'
      );
      return;
    }

    const totalAssetsIndex = this.getIndex(null, 'Total assets');
    const totalLiabilitiesIndex = this.getIndex(null, 'Total liabilities and shareholders equity');

    if (totalAssetsIndex === null || totalLiabilitiesIndex === null) {
      this.gtwSnackbarService.openSnackBar(
        'Unable to validate totals. Please refresh and try again.',
        'gtw-snackbar--red'
      );
      return;
    }

    const assetsColC = this.irsF5471ScheduleFData[totalAssetsIndex].col_c?.VALUE;
    const liabColC = this.irsF5471ScheduleFData[totalLiabilitiesIndex].col_c?.VALUE;

    const bothNull = assetsColC == null && liabColC == null;
    const equal = assetsColC != null && liabColC != null && parseInt(assetsColC.toString(), 10) === parseInt(liabColC.toString(), 10);

    if (!bothNull && !equal) {
      this.crudLoading = false;
      this.gtwSnackbarService.openSnackBar(
        "Total Assets should be equal to Total Liabilities and Stockholders' equity, Please Adjust the Amount.",
        'gtw-snackbar--red'
      );
      return;
    }

    this.crudLoading = true;
    const editRow: any[] = [];

    const scheduleSettings = [{
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      jcd_key: 250
    }];

    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    const groupObjKey = this.rowData?.GROUP_OBJ_KEY;

    for (let i = 0; i < this.irsF5471ScheduleFData.length - 1; i++) {
      const line = this.irsF5471ScheduleFData[i];
      if (!line.col_c || line.col_c.VALUE == null || !line.saveParams) continue;

      let value: number;
      if (this.line_arr.indexOf(line.line_no) !== -1 || line.line_type === 'Liabilities') {
        value = parseInt(line.col_c.VALUE.toString(), 10) * -1;
      } else {
        value = line.col_c.VALUE as number;
      }

      editRow.push([{
        tax_year: line.saveParams.tax_year,
        trans_type_key: line.saveParams.trans_type_key,
        combination_key: combinationKey,
        group_obj_key: groupObjKey,
        form_key: line.saveParams.form_key,
        line_no: line.line_no,
        occurence: line.saveParams.occurrence === '0' ? null : line.saveParams.occurrence,
        line_attrib_key: line.col_c.line_attrib_key,
        Attribute_Name: line.col_c.attrib_name,
        Attribute_Value: value,
        trans_dtls_key: line.saveParams.trans_Details_key === '0' ? null : line.saveParams.trans_Details_key,
        trans_status: 'A'
      }]);
    }

    if (this.irs5471ScheduleFNotes?.VALUE?.VALUE != null && this.irs5471ScheduleFNotes.saveParams) {
      const notes = this.irs5471ScheduleFNotes;
      const sp = notes.saveParams!;
      const val = notes.VALUE!;
      editRow.push([{
        tax_year: sp.tax_year,
        trans_type_key: sp.trans_type_key,
        combination_key: combinationKey,
        group_obj_key: groupObjKey,
        form_key: sp.form_key,
        line_no: notes.line_no,
        occurence: sp.occurrence === '0' ? null : sp.occurrence,
        line_attrib_key: val.line_attrib_key,
        Attribute_Name: val.attrib_name,
        Attribute_Value: val.VALUE,
        trans_dtls_key: sp.trans_Details_key === '0' ? null : sp.trans_Details_key,
        trans_status: 'A'
      }]);
    }

    const scheduleDetails = {
      addrows: [],
      editrows: editRow,
      removerows: []
    };

    const message = 'Schedule F details are successfully saved';

    const apiRoot = this.resolveApiRoot();
    this.fir5471ScheduleFService
      .saveScheduleF(
        apiRoot,
        scheduleSettings,
        scheduleDetails,
        this.globalService?.globalParams || {},
        this.filterParams
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: any) => {
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          this.crudLoading = false;
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.crudLoading = false;
          this.requestGridUpdate.emit('refresh');
          this.reinitialize();
        }
        this.cdr.markForCheck();
      });
  }

  reset(): void {
    this.crudLoading = false;
    this.irsF5471ScheduleFData = _.cloneDeep(this.irsF5471ScheduleFOriginalData);
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  getLinesByType(type: 'Assets' | 'Liabilities'): ScheduleFLineItem[] {
    return this.irsF5471ScheduleFData.filter(line => line.line_type === type);
  }

  trackByLineNo(index: number, item: ScheduleFLineItem): string {
    return item.line_no + (item.line_Description || '');
  }

  onColCChange(line_no: string): void {
    this.computeRowTotal(line_no);
  }

  onNotesChange(): void {
    // Notes are bound directly; no extra processing needed
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  reinitialize(): void {
    this.workspaceLoadSeq++;
    this.isInitialized = false;
    this.tryInit();
    this.cdr.markForCheck();
  }
}
